import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { LanguageProvider, detectPreferredLang } from '@/context/LanguageContext';
import { Header } from '@/components/site/Header';
import { Hero } from '@/components/site/Hero';
import { Marquee } from '@/components/site/Marquee';

// Below-the-fold sections — split into async chunks to reduce initial JS payload.
const Products  = lazy(() => import('@/components/site/Products').then(m => ({ default: m.Products })));
const Manifesto = lazy(() => import('@/components/site/Manifesto').then(m => ({ default: m.Manifesto })));
const Clients   = lazy(() => import('@/components/site/Clients').then(m => ({ default: m.Clients })));
const Contact   = lazy(() => import('@/components/site/Contact').then(m => ({ default: m.Contact })));
const Footer    = lazy(() => import('@/components/site/Footer').then(m => ({ default: m.Footer })));

const SectionFallback = () => <div style={{ minHeight: '60vh' }} />;

const Site = () => {
  const { hash } = useLocation();

  useEffect(() => {
    let lenis;
    let rafId;
    let cleanup = () => {};

    // Lenis is only loaded on desktop pointer devices to save mobile JS/CPU.
    const isDesktop = window.matchMedia('(min-width: 768px) and (pointer: fine)').matches;
    if (isDesktop) {
      import('lenis').then(({ default: Lenis }) => {
        lenis = new Lenis({
          duration: 1.15,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smoothWheel: true,
        });
        const raf = (time) => {
          lenis.raf(time);
          rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
      });
    }

    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href');
      if (id.length < 2) return;
      const el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        if (lenis) lenis.scrollTo(el, { offset: -60 });
        else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    document.addEventListener('click', onClick);
    cleanup = () => {
      cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
      document.removeEventListener('click', onClick);
    };
    return cleanup;
  }, []);

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const el = document.querySelector(hash);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }
  }, [hash]);

  return (
    <LanguageProvider>
      <div className="App grain">
        <Header />
        <main>
          <Hero />
          <Marquee />
          <Suspense fallback={<SectionFallback />}>
            <Products />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <Manifesto />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <Clients />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <Contact />
          </Suspense>
        </main>
        <Suspense fallback={<SectionFallback />}>
          <Footer />
        </Suspense>
      </div>
    </LanguageProvider>
  );
};

const LangGate = () => {
  const preferred = detectPreferredLang();
  return <Navigate to={`/${preferred}`} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LangGate />} />
        <Route path="/:lang" element={<Site />} />
        <Route path="*" element={<LangGate />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
